// @flow

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { trackEvent, ENTER_PAGE } from '../../../services/mixpanel';
// Blueprint components used via subcomponents
import Layout from '../../layouts/default-logged-in';
import { Panel, PanelBody } from '../../panel';
import {
  Chat,
  ReportPreviewPanel,
  TopBar,
  PromptFooter,
  ResizableSplit,
} from '../../ai-reports';
import AIReportsChatWrapper from '../../ai-reports/chat/AIReportsChatWrapper';
import { usePromptChatController, usePromptChatState, OrganisationContext } from '../../ai-prompt-management';
import { infoToast, successToast, errorToast } from '../../../utils/toaster';
import { saveDashboardReport, updateDashboardReport, fetch as fetchReports } from '../../../actions/reports';
import { Alert, Intent } from '@blueprintjs/core';
import * as ROUTES from '../../../constants/routes';
import { getSaveDisabled as getSaveDisabledUtil, computeSpecHash } from '../../ai-reports/utils/save-gating';
import { getAiReportsUiModel } from '../../ai-reports/ui-model';
import * as Selectors from '../../../selectors';
import { ORG_HAS_STAFF_DETECTION, DISABLE_AGE_GENDER } from '../../../constants/features';

const Wrapper = styled.div`
  display: flex; /* ensure children can flex vertically */
  flex-direction: column;
  flex: 1;
  /* Rely on parent layout height; avoid forcing 100vh to prevent outer scroll */
  min-height: 0;
  width: 100%; /* occupy full available width */
`;

const StretchPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  flex: 1; /* fill vertical space within padding */
  min-height: 0;
  overflow: hidden; /* prevent page-level scroll; let inner panes scroll */
  width: 100%;
  max-width: none; /* override any panel-level max width constraints */
`;

const BodyFlex = styled(PanelBody)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* contain overflow to ContentArea children */
  width: 100%;
  max-width: none;
  /* Remove horizontal padding so child panels (JSON + Chat) abut gutters */
  /* Match /my-reports bottom spacing by avoiding extra bottom padding here. */
  padding: 0.5rem 0 0;
  /* Explicitly match the page gutter color */
  background: var(--main-background);
`;

const ChatContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const GENERIC_ERROR_TOAST_MESSAGE = 'Something went wrong, please try again.';

// Component that uses chat controller (must be inside AIReportsChatWrapper)
const ChatWithControls = ({ inputRef, promptPlaceholder, controllerResetRef, onGetSessionId, onFirstInteraction, getPromptMaxChars }) => {
  const [prompt, setPrompt] = useState('');
  const controller = usePromptChatController();
  const state = usePromptChatState();
  const lastErrorRef = useRef(null);
  
  // Expose controller.reset to parent via ref
  useEffect(() => {
    if (controllerResetRef) {
      controllerResetRef.current = controller.reset;
    }
  }, [controller.reset, controllerResetRef]);
  
  // Expose sessionId to parent via callback
  useEffect(() => {
    if (onGetSessionId && state.sessionId) {
      onGetSessionId(state.sessionId);
    }
  }, [state.sessionId, onGetSessionId]);
  
  const maxPromptChars = useMemo(() => {
    if (getPromptMaxChars) return getPromptMaxChars(state.maxPromptChars);
    const n = Number(state.maxPromptChars);
    return Number.isFinite(n) && n > 0 ? n : 1000;
  }, [getPromptMaxChars, state.maxPromptChars]);

  const promptTooLong = useMemo(
    () => String(prompt || '').length > maxPromptChars,
    [prompt, maxPromptChars]
  );

  // Watch for chat errors and surface them via toast.
  // To avoid repeated toasts for the *same* error instance (e.g. parent re-renders),
  // we only fire when the error object reference changes.
  useEffect(() => {
    const err = state && state.error;
    const message = err && err.message;
    if (!message) return;

    if (lastErrorRef.current === err) return;
    lastErrorRef.current = err;

    // Use generic toast message so backend/internal messages are not exposed directly
    errorToast({ message: GENERIC_ERROR_TOAST_MESSAGE });
  }, [state && state.error]);

  const handleSend = useCallback(async () => {
    if (!prompt.trim()) return;
    if (onFirstInteraction) onFirstInteraction();
    await controller.send(prompt);
    setPrompt('');
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [prompt, controller, inputRef, onFirstInteraction]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', gap: '0.5rem' }}>
      <div style={{ flex: '1 1 auto', overflow: 'auto', width: '100%' }}>
        <Chat />
      </div>
      <div style={{ flex: '0 0 auto', width: '100%' }}>
        <PromptFooter
          inputRef={inputRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={onKeyDown}
          isLoading={state.isStreaming}
          onSend={handleSend}
          placeholder={promptPlaceholder}
          sendDisabled={state.isStreaming || promptTooLong || state.promptLocked}
          notice={
            promptTooLong
              ? `Prompt is too long (${String(prompt || '').length}/${maxPromptChars}). Please shorten your request.`
              : undefined
          }
          attribution=""
        />
      </div>
    </div>
  );
};

const AIReports = ({ dispatch, history, match, originalReport, organisationName, staffDetectionDisabled, demographicsDisabled, availableMetricGroups }) => {
  const location = useLocation();
  const inEditMode = !!(match && match.params && match.params.reportId);
  const editReportId = inEditMode ? match.params.reportId : null;
  const [reportJson, setReportJson] = useState(null);
  const [split, setSplit] = useState(0.5); // proportion of space for JSON panel when available
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [inputsEnabled, setInputsEnabled] = useState(false);
  const [currentSpecHash, setCurrentSpecHash] = useState(null);
  const [lastSavedSpecHash, setLastSavedSpecHash] = useState(null);
  const [baselineName, setBaselineName] = useState('');
  const [baselineDescription, setBaselineDescription] = useState('');
  const [baselineSpecHash, setBaselineSpecHash] = useState(null);
  const [draftConfig, setDraftConfig] = useState(null);
  const [resetEnabled, setResetEnabled] = useState(false);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(null); // 'cancel' | 'reset' | null
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  
  const ui = useMemo(() => getAiReportsUiModel({ inEditMode }), [inEditMode]);
  const promptPlaceholder = ui && ui.copy && ui.copy.chat && ui.copy.chat.promptPlaceholder;
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  const dividerRef = useRef(null);
  const controllerResetRef = useRef(null);

  // Track page view on mount
  useEffect(() => {
    trackEvent(ENTER_PAGE, 'REPORT_DESIGNER');
  }, []);

  // Focus on mount
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Show info message if redirected here with a message in state or query
  useEffect(() => {
    try {
      const stateMsg = location && location.state && location.state.message;
      const search = (location && location.search) || '';
      let queryMsg = '';
      if (search && typeof URLSearchParams !== 'undefined') {
        const params = new URLSearchParams(search);
        queryMsg = params.get('message') || '';
      }
      const msg = stateMsg || queryMsg;
      if (msg) {
        infoToast({ message: String(msg) });
      }
    } catch (e) {
      // best effort only
    }
  }, [location]);

  // When switching from Edit -> Create route, fully clear page-local state
  useEffect(() => {
    if (!inEditMode) {
      // Clear preview and inputs to empty for create mode landing
      setReportJson(null);
      setName('');
      setDescription('');
      setDraftConfig(null);
      setBaselineName('');
      setBaselineDescription('');
      setBaselineSpecHash(null);
      setCurrentSpecHash(null);
      setLastSavedSpecHash(null);
      setInputsEnabled(false);
      setResetEnabled(false);
      
      // Reset chat state (messages, sessionId, etc.) when navigating from Edit to Create
      // This ensures clean slate for new report creation
      if (controllerResetRef.current) {
        controllerResetRef.current();
      }
    }
  }, [inEditMode]);

  // Edit mode: seed from original report when available
  useEffect(() => {
    if (!inEditMode) return;
    if (!originalReport) return;
    // page-local copy used for preview/stream; do not mutate Redux state
    const orig = { ...originalReport };
    setReportJson({ id: orig.id, name: orig.name, description: orig.description, config: { ...(orig.config || {}) } });
    setName(orig.name || '');
    setDescription(orig.description || '');
    setInputsEnabled(true); // inputs enabled immediately in edit mode
    setBaselineName(orig.name || '');
    setBaselineDescription(orig.description || '');
    const baseHash = computeSpecHash({ draftConfig: null, reportJson: { config: orig.config || {} } });
    setBaselineSpecHash(baseHash);
    setCurrentSpecHash(baseHash);
    setLastSavedSpecHash(baseHash);
    setResetEnabled(false);
  }, [inEditMode, originalReport]);

  const performReset = useCallback(async () => {
    // Reset chat state via controller
    if (controllerResetRef.current) {
      controllerResetRef.current();
    }
    
    // Reset page-specific state
    if (inEditMode && originalReport) {
      const orig = originalReport;
      const reloaded = {
        id: orig.id,
        name: orig.name,
        description: orig.description,
        config: { ...(orig.config || {}) },
      };
      setReportJson(reloaded);
      setName(orig.name || '');
      setDescription(orig.description || '');
      setInputsEnabled(true);
      const baseHash = computeSpecHash({ reportJson: { config: orig.config || {} } });
      setBaselineName(orig.name || '');
      setBaselineDescription(orig.description || '');
      setBaselineSpecHash(baseHash);
      setCurrentSpecHash(baseHash);
      setLastSavedSpecHash(baseHash);
      setDraftConfig(null);
    } else {
      setReportJson(null);
      setInputsEnabled(false);
      setCurrentSpecHash(null);
      setLastSavedSpecHash(null);
      if (!inEditMode) {
        setName('');
        setDescription('');
      }
    }
    setResetEnabled(false);
    if (inputRef.current) inputRef.current.focus();
  }, [inEditMode, originalReport]);

  const performCancel = useCallback(async () => {
    if (!inEditMode || !editReportId) return;
    
    const owned = originalReport && !!originalReport.ownerId;
    const target = owned
      ? ROUTES.SINGLE_MY_REPORT.replace(':reportId', editReportId)
      : ROUTES.SINGLE_REPORT.replace(':reportId', editReportId);
    history && history.push && history.push(target);
  }, [inEditMode, editReportId, originalReport]);

  // Compute once per render so Save button and confirm dialog logic use the same value.
  // Includes SSE/stream-driven updates via reportJson/currentSpecHash.
  const isSaveDisabled = useMemo(
    () =>
      getSaveDisabledUtil({
        isLoading: false, // Now managed by ChatProvider, assume false for save logic
        inputsEnabled,
        reportJson,
        name,
        description,
        currentSpecHash,
        lastSavedSpecHash,
        inEditMode,
        baselineName,
        baselineDescription,
        baselineSpecHash,
      }),
    [
      inputsEnabled,
      reportJson,
      name,
      description,
      currentSpecHash,
      lastSavedSpecHash,
      inEditMode,
      baselineName,
      baselineDescription,
      baselineSpecHash,
    ],
  );

  const hasUnsavedChanges = !isSaveDisabled;

  const handleReset = useCallback(async () => {
    if (hasUnsavedChanges) {
      setUnsavedChangesDialog('reset');
      return;
    }

    await performReset();
  }, [performReset, hasUnsavedChanges]);

  // Stable callback to avoid regenerating function identity every keystroke, preventing unnecessary
  // re-renders of ReportPreviewPanel (and heavy WidgetGrid) when only prompt changes.
  const handleDerivedConfig = useCallback(
    (cfg) => {
      setDraftConfig(cfg || null);
      const nextHash = computeSpecHash({ draftConfig: cfg, reportJson });
      setCurrentSpecHash(nextHash);
    },
    [reportJson],
  );

  // Memoize preview pane subtree so that unrelated parent state (e.g. prompt typing) does not cause
  // React to recreate the element tree; only changes when its meaningful inputs update.
  const previewPane = useMemo(
    () => (
      <div
        style={{
          flex: '1 1 auto',
          overflow: 'auto',
          width: '100%',
          height: '100%', // ensure placeholder (height:100%) can fill available space
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ReportPreviewPanel
          reportJson={reportJson}
          onDerivedConfigChange={handleDerivedConfig}
        />
      </div>
    ),
    [reportJson, handleDerivedConfig],
  );

  const orgContextValue = useMemo(() => ({ organisationName }), [organisationName]);

  return (
    <OrganisationContext.Provider value={orgContextValue}>
      <Layout>
      <Wrapper>
        <StretchPanel style={{ width: '100%' }}>
          <TopBar
            onReset={handleReset}
            disabled={false}
            inEditMode={inEditMode}
            resetEnabled={inEditMode ? resetEnabled : undefined}
            name={name}
            onNameChange={setName}
            description={description}
            onDescriptionChange={setDescription}
            inputsEnabled={
              // Enable editing name/description only when inputs are generally enabled
              // For edit mode: enabled as soon as original is loaded.
              // For create mode: after first validation.
              !!inputsEnabled
            }
            ui={ui}
            onCancel={() => {
              if (!inEditMode || !editReportId) return;
              // If Save is enabled, there are unsaved changes; show confirm
              if (hasUnsavedChanges) {
                setUnsavedChangesDialog('cancel');
                return;
              }
              performCancel();
            }}
            onSave={async () => {
              // Guard double clicks via disabled state but keep checks here
              if (isSaveDisabled) return;
              try {
                const effectiveConfig = draftConfig || (reportJson && reportJson.config) || {};
                if (inEditMode && editReportId) {
                  await dispatch(
                    updateDashboardReport(editReportId, {
                      name,
                      description,
                      config: effectiveConfig,
                    }),
                  );
                  successToast({ message: 'Report updated.', timeout: 2500 });
                  // Refresh lists explicitly (thunk already does, but ensure latest)
                  await dispatch(fetchReports({ fresh: true }));
                  const owned = originalReport && !!originalReport.ownerId;
                  const target = owned
                    ? ROUTES.SINGLE_MY_REPORT.replace(':reportId', editReportId)
                    : ROUTES.SINGLE_REPORT.replace(':reportId', editReportId);
                  history && history.push && history.push(target);
                } else {
                  await dispatch(
                    saveDashboardReport({
                      name,
                      description,
                      config: effectiveConfig,
                    }),
                  );
                  successToast({ message: 'Report saved.', timeout: 2500 });
                  setLastSavedSpecHash(currentSpecHash);
                  // In create mode, disable name/description until the next validated report
                  setInputsEnabled(false);
                }
              } catch (e) {
                // Errors are handled (toasted) inside actions; keep catch for control flow only
              }
            }}
            saveDisabled={isSaveDisabled}
            onSaveAsNew={inEditMode ? async () => {
              // Create a new report from current edits, remain on this page
              try {
                const effectiveConfig = draftConfig || (reportJson && reportJson.config) || {};
                const created = await dispatch(
                  saveDashboardReport({
                    name,
                    description,
                    config: effectiveConfig,
                  }),
                );
                successToast({ message: 'Report saved.', timeout: 2500 });
                setLastSavedSpecHash(currentSpecHash);
                // Navigate to the newly created report
                const newId = created && created.id;
                if (newId) {
                  const target = ROUTES.SINGLE_MY_REPORT.replace(':reportId', newId);
                  history && history.push && history.push(target);
                }
              } catch (e) {
                // Errors are handled (toasted) inside actions; keep catch for control flow only
              }
            } : undefined}
          />

          <BodyFlex>
            <ResizableSplit
              initialSplit={split}
              onSplitChange={setSplit}
              min={0.15}
              max={0.92}
              contentRef={contentRef}
              dividerRef={dividerRef}
              collapsed={chatCollapsed}
              top={
                <div style={{ flex: '1 1 auto', overflow: 'auto', width: '100%' }}>
                  {previewPane}
                </div>
              }
              bottom={
                <AIReportsChatWrapper
                  mode={inEditMode ? 'edit' : 'create'}
                  reportId={inEditMode ? editReportId : undefined}
                  sessionId={currentSessionId}
                  onSessionIdChange={setCurrentSessionId}
                  staffDetectionDisabled={staffDetectionDisabled}
                  demographicsDisabled={demographicsDisabled}
                  availableMetricGroups={availableMetricGroups}
                  onReportValidated={(data) => {
                    if (data) {
                      const nextReport = { ...(typeof data === 'object' ? data : {}) };
                      setReportJson(nextReport);
                      if (typeof nextReport.name === 'string') setName(nextReport.name);
                      if (typeof nextReport.description === 'string') setDescription(nextReport.description);
                      if (!inEditMode) setInputsEnabled(true);
                      const hash = computeSpecHash({ draftConfig: null, reportJson: nextReport });
                      setCurrentSpecHash(hash);
                      if (inEditMode) setResetEnabled(true);
                    }
                  }}
                  onError={(error) => {
                    // Errors are shown as toasts (handled in ChatWithControls)
                  }}
                >
                  <ChatContainer>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      <ChatWithControls 
                        inputRef={inputRef}
                        promptPlaceholder={promptPlaceholder}
                        controllerResetRef={controllerResetRef}
                        onGetSessionId={setCurrentSessionId}
                        getPromptMaxChars={ui && ui.limits && ui.limits.getPromptMaxChars}
                        onFirstInteraction={() => {
                          if (chatCollapsed) setChatCollapsed(false);
                        }}
                      />
                    </div>
                  </ChatContainer>
                </AIReportsChatWrapper>
              }
            />
          </BodyFlex>
        </StretchPanel>
        {/* Unified unsaved changes confirmation dialog */}
        <Alert
          cancelButtonText="Keep editing"
          confirmButtonText="Discard changes"
          icon="warning-sign"
          intent={Intent.WARNING}
          isOpen={!!unsavedChangesDialog}
          onCancel={() => {
            setUnsavedChangesDialog(null);
          }}
          onConfirm={() => {
            const action = unsavedChangesDialog;
            setUnsavedChangesDialog(null);
            if (action === 'cancel') {
              performCancel();
            } else if (action === 'reset') {
              performReset();
            }
          }}
        >
          <p>
            {unsavedChangesDialog === 'cancel'
              ? 'You have unsaved changes. If you cancel now, your changes will be lost. Do you want to discard changes and go back to the report?'
              : 'You have unsaved changes. If you reset now, your changes will be lost. Do you want to discard changes and reset to the original report?'}
          </p>
        </Alert>
      </Wrapper>
    </Layout>
    </OrganisationContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  organisationName: Selectors.getOrganisationName(state),
  staffDetectionDisabled: !Selectors.userHasFeature(ORG_HAS_STAFF_DETECTION, state),
  demographicsDisabled: Selectors.userHasFeature(DISABLE_AGE_GENDER, state),
  availableMetricGroups: Selectors.getAvailableMetricGroups(state),
});

export default connect(mapStateToProps)(AIReports);
